import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/joy";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { gradientPairs } from "../../../theme";
import GuestNavbar from "../../../components/Layout/GuestNavbar";

const HERO_BG = "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)";
const PINK_ALERT_BG = "rgba(244, 114, 182, 0.12)";
const PINK_ALERT_BORDER = "rgba(244, 114, 182, 0.35)";
const INFO_ICON_BG = "#3b82f6";

const InfoBlock = ({
    children,
    highlight,
}: {
    children: React.ReactNode;
    highlight?: boolean;
}) => (
    <Card
        variant="outlined"
        sx={{
            padding: 2,
            borderRadius: 2,
            height: "100%",
            position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            borderColor: highlight ? "danger.300" : "divider",
        }}
        data-no-glow
        data-no-animation
    >
        <Box
            sx={{
                position: "absolute",
                top: 12,
                left: 12,
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: INFO_ICON_BG,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Info size={16} />
        </Box>
        <Box sx={{ pl: 4 }}>{children}</Box>
    </Card>
);

const Tef = () => {
    const navigate = useNavigate();

    return (
        <Stack sx={{ minHeight: "100vh", overflowX: "hidden", bgcolor: "background.body" }}>
            <GuestNavbar />

            {/* Hero banner */}
            <Box
                sx={{
                    width: "100%",
                    paddingY: 5,
                    paddingX: 2,
                    background: HERO_BG,
                    backgroundImage: `
                        ${HERO_BG},
                        radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
                        radial-gradient(circle at 80% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)
                    `,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    level="h1"
                    sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        fontWeight: 700,
                        color: "white",
                        textAlign: "center",
 letterSpacing: "-0.02em",
                    }}
                >
                    TEF & TEF Canada
                </Typography>
            </Box>

            {/* Main content */}
            <Stack
                paddingX={{ xs: 2, sm: 4, lg: 6 }}
                paddingY={4}
                gap={4}
                maxWidth={1000}
                margin="0 auto"
                width="100%"
                sx={{ bgcolor: "background.surface" }}
            >
                {/* Pink alert - Important notice */}
                <Alert
                    variant="soft"
                    sx={{
                        bgcolor: PINK_ALERT_BG,
                        border: "1px solid",
                        borderColor: PINK_ALERT_BORDER,
                        borderRadius: 2,
                        padding: 2.5,
                        "& .JoyAlert startDecorator": { display: "none" },
                    }}
                >
                    <Typography level="body-md" textColor="neutral.700">
                        <strong>Important information.</strong> The TEF and TEF Canada are official French
                        proficiency tests. Exam sessions and registration are managed by authorised test
                        centres (e.g. Alliance Française, CCI Paris Île-de-France). LinguaScore is a
                        preparation platform: we help you get ready with practice materials and mock tests
                        aligned to the exam format. For exam dates, fees and registration, contact your
                        local test centre.
                    </Typography>
                </Alert>

                {/* TEF logo + intro row */}
                <Stack direction={{ xs: "column", md: "row" }} gap={3} alignItems={{ md: "flex-start" }}>
                    <Box
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: "#6366f1",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "1.5rem",
                                fontWeight: 700,
                            }}
                        >
                            *
                        </Box>
                        <Typography
                            level="body-sm"
                            sx={{
                                color: "#0ea5e9",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.02em",
                                maxWidth: 140,
                            }}
                        >
                            TEF Test d&apos;Évaluation de Français
                        </Typography>
                    </Box>
                    <Typography level="body-md" textColor="neutral.600" sx={{ flex: 1 }}>
                        The <strong>TEF</strong> (Test d&apos;Évaluation de Français) is an international
                        assessment of proficiency in the French language. It is used in particular by
                        Citizenship and Immigration Canada to assign points for the language factor when
                        applying for citizenship or permanent residency. The test is suitable for
                        individuals aged 16 and above. For immigration inquiries, please visit the
                        official CIC website.
                    </Typography>
                </Stack>

                {/* TEF + TEF Canada two-column cards */}
                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                                height: "100%",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 1.5 }}>
                                TEF
                            </Typography>
                            <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                                Directed at candidates whose native language is not French and who wish to
                                assess their level of French, the TEF can be used for academic studies,
                                emigration, citizenship applications, and professional objectives.
                            </Typography>
                            <Typography level="title-sm" sx={{ marginBottom: 1 }}>
                                Three compulsory tests (2h10 total), multiple-choice (QCM):
                            </Typography>
                            <List size="sm" sx={{ "--ListMarkerSize": "6px", marginBottom: 2 }}>
                                <ListItem><strong>Reading:</strong> 60 min, 40 questions, 300 points</ListItem>
                                <ListItem><strong>Listening:</strong> 40 min, 40 questions, 360 points</ListItem>
                                <ListItem><strong>Vocabulary &amp; Structure:</strong> 30 min, 40 questions, 240 points</ListItem>
                            </List>
                            <Typography level="title-sm" sx={{ marginBottom: 1 }}>
                                Two optional tests (1h20 total):
                            </Typography>
                            <List size="sm" sx={{ "--ListMarkerSize": "6px" }}>
                                <ListItem><strong>Writing:</strong> 60 min, 2 subjects, 450 points</ListItem>
                                <ListItem><strong>Speaking:</strong> 20 min, 2 subjects, 450 points</ListItem>
                            </List>
                        </Card>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                                height: "100%",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 1.5 }}>
                                TEF Canada
                            </Typography>
                            <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                                Directed specifically at candidates wishing to assess their level of French
                                for immigration to Canada, the TEF Canada comprises two compulsory tests
                                (1h40 total), multiple-choice (QCM):
                            </Typography>
                            <Typography level="title-sm" sx={{ marginBottom: 1 }}>
                                Compulsory:
                            </Typography>
                            <List size="sm" sx={{ "--ListMarkerSize": "6px", marginBottom: 2 }}>
                                <ListItem><strong>Reading:</strong> 60 min, 40 questions, 300 points</ListItem>
                                <ListItem><strong>Listening:</strong> 40 min, 40 questions, 360 points</ListItem>
                            </List>
                            <Typography level="title-sm" sx={{ marginBottom: 1 }}>
                                Optional:
                            </Typography>
                            <List size="sm" sx={{ "--ListMarkerSize": "6px" }}>
                                <ListItem><strong>Writing:</strong> 60 min, 2 subjects, 450 points</ListItem>
                                <ListItem><strong>Speaking:</strong> 20 min, 2 subjects, 450 points</ListItem>
                            </List>
                        </Card>
                    </Grid>
                </Grid>

                {/* Important Information - grid of info blocks */}
                <Box>
                    <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 2 }}>
                        Important Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock highlight>
                                <Typography level="body-sm" textColor="neutral.700" sx={{ marginBottom: 1 }}>
                                    The exam takes place at your chosen test centre. Confirm the exact
                                    address and room on your official invitation.
                                </Typography>
                                <Typography level="body-sm" color="danger" fontWeight={700}>
                                    It is your responsibility to verify the address on your official invitation.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    The TEF is <strong>valid for 2 years</strong> from the date of the test.
                                    You may take the TEF as many times as you wish with at least{" "}
                                    <strong>20 days</strong> between two takes in any location.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    You will get results within <strong>6 weeks</strong>: an electronic
                                    version will be sent to your email from CCI Paris. There is no way to
                                    speed up the process as all information is sent to Paris for grading.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    The <strong>reading, listening and writing</strong> portions are
                                    computer-based. The <strong>speaking</strong> portion takes place
                                    in person with an assessor. French accents are available on the
                                    platform.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    A few days before your exam, you will receive an official convocation
                                    with your schedule. It is essential you arrive at the time indicated
                                    on your invitation; you will not be granted access after the exam starts.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12} md={6} lg={4}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    For information about which test is required for immigration,
                                    citizenship or other purposes, please inquire with the immigration
                                    services or the appropriate institution.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                        <Grid xs={12}>
                            <InfoBlock>
                                <Typography level="body-sm" textColor="neutral.700">
                                    Exam organisation requires the collaboration of institutions including
                                    the Chambre de Commerce et d&apos;Industrie de Paris (CCIP). In very rare
                                    cases, unforeseen technical issues may lead to session changes or
                                    cancellations. Test centres cannot be held responsible for travel or
                                    accommodation expenses. We encourage you to register at an authorised
                                    centre close to your place of residence when possible.
                                </Typography>
                            </InfoBlock>
                        </Grid>
                    </Grid>
                </Box>

                {/* Exam Day Guidelines */}
                <Card
                    variant="outlined"
                    sx={{ padding: 3, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                    data-no-glow
                    data-no-animation
                >
                    <Typography level="h2" sx={{ fontSize: "1.35rem", marginBottom: 2 }}>
                        Exam Day Guidelines
                    </Typography>
                    <List
                        component="ol"
                        size="sm"
                        sx={{ listStyle: "decimal", pl: 2.5, "--ListMarkerSize": "1.5em", gap: 0.5 }}
                    >
                        <ListItem>
                            <strong>Timely check-in:</strong> Present a valid ID (passport, permanent
                            resident card, or driver&apos;s license) and your schedule notification. Without
                            valid ID, entry is denied. Late arrivals are not allowed; no refunds or
                            deferments.
                        </ListItem>
                        <ListItem>
                            <strong>ID verification:</strong> After the ID check, you will sign the required
                            documents and have an identification photo taken.
                        </ListItem>
                        <ListItem>
                            <strong>Exam room:</strong> You will be guided to the room. Personal items such
                            as phones and watches must be stored away.
                        </ListItem>
                        <ListItem>
                            <strong>Pre-test briefing:</strong> The supervisor will give final instructions.
                        </ListItem>
                        <ListItem>
                            <strong>Conduct:</strong> No exits or communication between candidates are
                            allowed, except for medically documented emergencies. Latecomers will be denied
                            entry.
                        </ListItem>
                        <ListItem>
                            <strong>Results:</strong> Official certificates will be sent by email within
                            the timeframe communicated by your test centre (typically within 6 weeks).
                        </ListItem>
                    </List>
                </Card>

                {/* CTA - Prepare with LinguaScore */}
                <Card
                    variant="soft"
                    sx={{
                        padding: 3,
                        borderRadius: 2,
                        bgcolor: "primary.50",
                        borderColor: "primary.200",
                    }}
                    data-no-glow
                    data-no-animation
                >
                    <Typography level="title-lg" sx={{ marginBottom: 1 }}>
                        Prepare with LinguaScore
                    </Typography>
                    <Typography level="body-md" textColor="neutral.600" sx={{ marginBottom: 2 }}>
                        Get ready for the TEF and TEF Canada with practice materials, mock tests and
                        progress tracking. Our content is designed to familiarise you with the exam format
                        and help you reach your target score.
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                        <Button
                            size="lg"
                            onClick={() => navigate("/register")}
                            sx={{
                                background: `linear-gradient(to right, ${gradientPairs[0][0]}, ${gradientPairs[0][1]})`,
                            }}
                        >
                            Start preparing
                        </Button>
                        <Button size="lg" variant="outlined" onClick={() => navigate("/pricing")}>
                            View pricing
                        </Button>
                    </Stack>
                </Card>
            </Stack>
        </Stack>
    );
};

export default Tef;
